import { Navigate, useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LoadingScreen,
} from "@eduviaos/ui-kit";
import { useAuth } from "./AuthProvider";

/**
 * Route guard for the super-admin portal. Beyond the standard
 * authenticated check, it enforces `role === "super_admin"`. A
 * tenant owner/admin/staff user reaching this URL will see a forbidden
 * card with a sign-out button rather than a flashing redirect — they
 * have no other valid landing in this app.
 */
export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status, user, logout } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <LoadingScreen label="Restoring session…" />;
  }
  if (status === "anonymous") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (user?.role !== "super_admin") {
    return (
      <main className="bg-muted/30 grid min-h-svh place-items-center px-4">
        <Card className="w-full max-w-md border-rose-500/30">
          <CardHeader>
            <div className="mb-2 grid size-9 place-items-center rounded-md bg-rose-500/10 text-rose-600 dark:text-rose-400">
              <ShieldAlert className="size-5" aria-hidden="true" />
            </div>
            <CardTitle>Access denied</CardTitle>
            <CardDescription>
              This portal is restricted to platform super administrators. Your account (
              {user?.email}) carries the role{" "}
              <code className="bg-muted rounded px-1 py-0.5 text-[11px]">{user?.role}</code>.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2">
            <Button onClick={logout} variant="outline">
              Sign out
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }
  return <>{children}</>;
}
